import React, { useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Typography, useMediaQuery, TextField, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import StoreSelector from "../components/StoreSelector";
import DataTable from "../components/DataTable";
import PropTypes from "prop-types";
import { formatAppDateTime } from "../utils/formatAppDateTime";
import useApi from "../hooks/useApi";
import { useUserContext } from "../contexts/userContext";
import { useStoreContext } from "../contexts/storeContext";
import { ReactComponent as PrintLabelIcon } from "../images/print-label-icon.svg";
import { generatePiiReportPdf } from "../utils/piiReportPdf";
import { resolvePiiPdfLanguage } from "../utils/resolvePiiPdfLanguage";
import toastr from "toastr";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    color: "#212529",
    fontSize: 24,
    fontFamily: "Inter",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
    letterSpacing: "-0.24px",
  },
  searchCard: {
    backgroundColor: "#F8F9FA",
    borderRadius: "8px",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
    maxWidth: "588px",
    border: "1px solid #DEE2E6",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  inputRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2),
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  inputField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      color: "#212529",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#CED4DA",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#ADB5BD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6F42C1",
        borderWidth: "1px",
      },
      "& input::placeholder": {
        color: "#ADB5BD",
        opacity: 1,
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
      },
    },
    "& .MuiInputLabel-outlined": {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      color: "#495057",
      "&.Mui-focused": {
        color: "#6F42C1",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1.25, 1.5),
    },
  },
  label: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#212529",
    marginBottom: theme.spacing(0.75),
    display: "block",
  },
  dateRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: theme.spacing(2),
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  dateField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      color: "#212529",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#CED4DA",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#ADB5BD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#6F42C1",
        borderWidth: "1px",
      },
      "& input::placeholder": {
        color: "#ADB5BD",
        opacity: 1,
        fontFamily: "Inter, sans-serif",
        fontSize: "14px",
      },
    },
    "& .MuiInputLabel-outlined": {
      fontFamily: "Inter, sans-serif",
      fontSize: "14px",
      color: "#495057",
      "&.Mui-focused": {
        color: "#6F42C1",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1.25, 1.5),
    },
    "& .MuiInputAdornment-root": {
      marginLeft: 0,
    },
  },
  dateLabel: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#212529",
    marginBottom: theme.spacing(0.75),
    display: "block",
    "&::after": {
      content: '"*"',
      color: "#DC3545",
      marginLeft: theme.spacing(0.5),
    },
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-start",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  clearButton: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    textTransform: "none",
    border: "1px solid #A78BFA",
    color: "#A78BFA",
    backgroundColor: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#F8F9FA",
      borderColor: "#A78BFA",
    },
  },
  searchButton: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    padding: theme.spacing(1, 2),
    borderRadius: "6px",
    textTransform: "none",
    backgroundColor: "#6F42C1",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#5A32A3",
    },
  },
  tableCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    padding: theme.spacing(3),
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  },
  expandedContent: {
    padding: theme.spacing(2.5),
    backgroundColor: "#FFFFFF",
    borderTop: "1px solid #E9ECEF",
  },
  sectionTitle: {
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    fontWeight: 600,
    color: "#495057",
    marginBottom: "8px",
  },
  infoCard: {
    border: "1px solid #E9ECEF",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: "8px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 0,
    border: "1px solid #E9ECEF",
    borderRadius: "8px",
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
    "@media (min-width: 769px)": {
      gridTemplateColumns: "minmax(0, 1fr) 1px minmax(0, 1fr)",
    },
  },
  infoBlockTitle: {
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    color: "#212529",
    lineHeight: "100%",
    marginBottom: theme.spacing(2.5),
  },
  infoColumn: {
    "@media (min-width: 769px)": {
      padding: theme.spacing(2),
    },
    "@media (max-width: 768px)": {
      padding: theme.spacing(2),
    },
  },
  infoColumnDivider: {
    "@media (min-width: 769px)": {
      alignSelf: "stretch",
      width: 1,
      backgroundColor: "#E9ECEF",
    },
  },
  infoRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginBottom: "8px",
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "100%",
    color: "#6C757D",
  },
  infoLabel: {
    minWidth: 90,
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "100%",
    letterSpacing: "0%",
    color: "#3A3F47",
  },
  infoValue: {
    color: "#212529",
    fontWeight: 500,
    marginBottom: "8px",
  },
  deviceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(2),
    "@media (max-width: 900px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },
  deviceLabel: {
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "100%",
    color: "#6C757D",
    marginBottom: "8px",
  },
  deviceValue: {
    fontFamily: "Inter, sans-serif",
    fontSize: "20px",
    color: "#212529",
    fontWeight: 600,
    lineHeight: "100%",
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8, 2),
    minHeight: "300px",
  },
  emptyIcon: {
    width: 64,
    height: 64,
    color: "#ADB5BD",
    marginBottom: theme.spacing(2),
  },
  emptyText: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#ADB5BD",
  },
}));

const PIIReports = ({ onTabChange }) => {
  const { t, i18n } = useTranslation();
  const isDesktop = useMediaQuery("(min-width:769px)");
  const classes = useStyles();
  const { request } = useApi();
  const { selectedStore } = useStoreContext();
  const { user } = useUserContext();
  const [formData, setFormData] = useState({
    imei: "",
    tradeInId: "",
  });
  const [tableData, setTableData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const isGeneratingPdfRef = useRef(false);

  const handleInputChange = (field) => (e) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const handleClear = () => {
    setFormData({
      imei: "",
      tradeInId: "",
    });
    setTableData([]);
    setTotalItems(0);
    setErrorMessage("");
  };

  const handleSearch = async () => {
    const storeId = user?.store?.id;
    if (!storeId) {
      setErrorMessage("Please select a store.");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const data = await request({
        path: "/report/pii",
        params: {
          imei: formData.imei || undefined,
          tradeInId: formData.tradeInId || undefined,
          page: 1,
          pageSize: 10,
        },
        headers: { "X-Store-Id": storeId },
      });

      const report = Array.isArray(data?.piiReport) ? data.piiReport : [];
      setTableData(report);
      setTotalItems(Number.isFinite(data?.total) ? data.total : report.length);
    } catch (error) {
      setTableData([]);
      setTotalItems(0);
      let errorText = error?.message || "Failed to load PII reports. Please try again.";
      try {
        const parsed = JSON.parse(errorText);
        if (parsed?.message) {
          errorText = parsed.message;
        }
      } catch (parseError) {
        // ignore JSON parse errors
      }
      setErrorMessage(errorText);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePDF = async (row) => {
    if (isGeneratingPdfRef.current) {
      return;
    }
    isGeneratingPdfRef.current = true;
    try {
      const pdfLanguage = resolvePiiPdfLanguage(i18n.language);
      const result = await generatePiiReportPdf(row, {
        language: pdfLanguage,
      });
      if (result?.englishFallback) {
        toastr.options = {
          closeButton: true,
          positionClass: "toast-top-center",
          timeOut: "6000",
        };
        toastr.info(
          t("piiPdf_fallbackEnglishToast", {
            defaultValue:
              "Report downloaded in English because the Japanese preview could not be rendered in your browser.",
          })
        );
      }
    } catch (e) {
      console.error("PII PDF generation failed:", e);
      toastr.options = {
        closeButton: true,
        positionClass: "toast-top-center",
        timeOut: "6000",
      };
      toastr.error(
        e?.message || "Could not generate the report. Please try again."
      );
    } finally {
      isGeneratingPdfRef.current = false;
    }
  };

  const tableColumns = useMemo(
    () => [
    {
      key: "tradeInId",
      label: t("piiPage_colTradeInNo"),
      sortable: true,
      render: (value) => value || t("piiPdf_emDash"),
    },
    { key: "imei", label: t("piiPage_colImei"), sortable: true },
    { key: "device", label: t("piiPage_colDevice"), sortable: true },
    {
      key: "customerName",
      label: t("piiPage_colCustomerName"),
      sortable: true,
      render: (value, row) => {
        const rawName = value || row?.customerInfo?.fullName || "";
        const cleaned = String(rawName).trim();
        if (!cleaned || cleaned.toLowerCase() === "undefined undefined") {
          return t("piiPdf_emDash");
        }
        return cleaned;
      },
    },
    {
      key: "tradeInDate",
      label: t("piiPage_colTradeInDate"),
      sortable: true,
      render: (value) => {
        if (!value) return t("piiPdf_emDash");
        return formatAppDateTime(value, i18n.language, "MMM D, YYYY");
      },
    },
    {
      key: "action",
      label: t("piiPdf_tableAction"),
      sortable: false,
      render: (value, row) => (
        <div 
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              generatePDF(row);
            }
          }}
          style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            generatePDF(row);
          }}
        >
          <PrintLabelIcon
            style={{
              width: "20px",
              height: "20px",
              color: "#7650E1",
            }}
          />
          <span
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontStyle: "normal",
              fontSize: "13px",
              lineHeight: "100%",
              letterSpacing: "0%",
              verticalAlign: "middle",
              color: "#7650E1",
            }}
          >
            {t("piiPdf_actionReport")}
          </span>
        </div>
      ),
    },
  ],
    [t, i18n.language]
  );

  const renderExpandedContent = (row) => {
    const customerInfo = row.customerInfo || {};
    const deviceDetails = row.deviceDetails || {};
    const normalizeText = (value) => {
      if (value === null || value === undefined) return "";
      const text = String(value).trim();
      if (!text || text.toLowerCase() === "undefined") return "";
      if (text.toLowerCase() === "undefined undefined") return "";
      return text;
    };
    const formatAddress = (value) => {
      const normalized = normalizeText(value);
      if (!normalized) return null;
      // Split by comma and filter out empty strings
      const parts = normalized.split(',').map(part => part.trim()).filter(part => part);
      return parts;
    };
    const safeValue = (value) => normalizeText(value) || t("piiPdf_emDash");

    return (
      <div className={classes.expandedContent}>
        <Typography className={classes.sectionTitle}>
          {t("piiPage_customerInformation")}
        </Typography>
        <div className={classes.infoCard} style={{ border: "none", padding: 0 }}>
          <div className={classes.infoGrid}>
            <div className={classes.infoColumn}>
              <Typography className={classes.infoBlockTitle}>
                {t("piiPdf_personalInformation")}
              </Typography>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>{t("piiPage_fullName")}</span>
                <span className={classes.infoValue}>
                  {safeValue(customerInfo.fullName)}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>{t("piiPage_age")}</span>
                <span className={classes.infoValue}>
                  {safeValue(customerInfo.age)}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Occupation</span>
                <span className={classes.infoValue}>
                  {safeValue(customerInfo.occupation)}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>{t("piiPage_formOfId")}</span>
                <span className={classes.infoValue}>
                  {safeValue(customerInfo.formOfId)}
                </span>
              </div>
            </div>
            <div className={classes.infoColumnDivider} />
            <div className={classes.infoColumn}>
              <Typography className={classes.infoBlockTitle}>
                {t("piiPage_contactInformation")}
              </Typography>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>{t("piiPage_address")}</span>
                <span className={classes.infoValue} style={{ marginBottom: "8px", display: "block" }}>
                  {(() => {
                    const addressParts = formatAddress(customerInfo.address);
                    return addressParts && addressParts.length > 0 ? (
                      <div style={{ marginBottom: "8px" }}>
                        {addressParts.map((part, index) => (
                          <span key={index} style={{ display: "block", marginBottom: "8px" }}>
                            {part}
                          </span>
                        ))}
                      </div>
                    ) : (
                      "—"
                    );
                  })()}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Contact No.</span>
                <span className={classes.infoValue}>
                  {safeValue(customerInfo.contactNo)}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>{t("piiPage_emailAddress")}</span>
                <span className={classes.infoValue}>
                  {safeValue(customerInfo.email)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Typography className={classes.sectionTitle}>Device Details</Typography>
        <div className={classes.infoCard}>
          <div className={classes.deviceGrid}>
            <div>
              <div className={classes.deviceLabel}>Manufacturer</div>
              <div className={classes.deviceValue}>
                {safeValue(deviceDetails.manufacturer)}
              </div>
            </div>
            <div>
              <div className={classes.deviceLabel}>{t("piiPage_storage")}</div>
              <div className={classes.deviceValue}>
                {safeValue(deviceDetails.storage)}
              </div>
            </div>
            <div>
              <div className={classes.deviceLabel}>IMEI</div>
              <div className={classes.deviceValue}>
                {safeValue(deviceDetails.imei || row.imei)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography
          className={classes.title}
          component="h1"
          data-pdf-lang-probe
        >
          {t("piiPage_title")}
        </Typography>
        <StoreSelector />
      </div>

      {/* Search/Filter Form Card */}
      <div className={classes.searchCard}>
        <div className={classes.formContainer}>
          <div className={classes.inputRow}>
            <div>
              <label className={classes.label}>{t("piiPage_colImei")}</label>
              <TextField
                fullWidth
                variant="outlined"
                className={classes.inputField}
                value={formData.imei}
                onChange={handleInputChange("imei")}
                placeholder={t("piiPage_placeholderImei")}
              />
            </div>
            <div>
              <label className={classes.label}>{t("piiPage_tradeInIdLabel")}</label>
              <TextField
                fullWidth
                variant="outlined"
                className={classes.inputField}
                value={formData.tradeInId}
                onChange={handleInputChange("tradeInId")}
                placeholder={t("piiPage_placeholderTradeInId")}
              />
            </div>
          </div>

          <div className={classes.buttonContainer}>
            <Button className={classes.clearButton} onClick={handleClear}>
              {t("piiPage_clear")}
            </Button>
            <Button
              className={classes.searchButton}
              onClick={handleSearch}
              variant="contained"
              disabled={isLoading || (!formData.imei && !formData.tradeInId)}
            >
              {isLoading ? t("piiPage_searching") : t("piiPage_search")}
            </Button>
          </div>
        </div>
      </div>

      {/* Results Table Card */}
      <div className={classes.tableCard}>
        <DataTable
          columns={tableColumns}
          data={tableData}
          showCheckbox={false}
          pagination={false}
          totalItems={totalItems}
          showExpandIcon={true}
          renderExpandedContent={renderExpandedContent}
          isLoading={isLoading}
          emptyState={
            <div className={classes.emptyState}>
              {errorMessage ? (
                <Typography className={classes.emptyText}>
                  {errorMessage}
                </Typography>
              ) : (
                <>
                  <svg
                    className={classes.emptyIcon}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z"
                      fill="currentColor"
                    />
                  </svg>
                  <Typography className={classes.emptyText}>
                    {t("piiPage_noResults")}
                  </Typography>
                </>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};

PIIReports.propTypes = {
  onTabChange: PropTypes.func,
};

PIIReports.defaultProps = {
  onTabChange: null,
};

export default PIIReports;

